import { addDays } from 'date-fns';
import { TZDateMini } from '@date-fns/tz';

/**
 * Generic IANA-timezone primitives used by the operating-hours logic.
 *
 * This module is domain-agnostic: it knows nothing about schedules or
 * opening/closing. It exists so that `operatingHours.ts` can treat timezone
 * conversion as a small, testable toolkit rather than inline plumbing.
 */

export type TimeZoneDateParts = {
  year: number;
  month: number;
  day: number;
  weekday: number;
  hour: number;
  minute: number;
  second: number;
};

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
export const DAY_IN_SECONDS = 24 * SECONDS_PER_HOUR;

export const DEFAULT_LOCATION_TIME_ZONE = 'UTC';

// Validation hits `new Intl.DateTimeFormat`, which is comparatively expensive.
const validatedTimeZoneCache = new Set<string>();

/**
 * Calendar fields for `date` as they appear on the clock in `timeZone`.
 *
 * `date` is a UTC instant; we need wall-clock fields in the target zone. We
 * use `TZDateMini` so getters read in the provided IANA timezone (not the
 * runtime's local timezone).
 */
export const getDatePartsInTimeZone = (
  date: Date,
  timeZone: string,
): TimeZoneDateParts => {
  const zoned = new TZDateMini(date.getTime(), timeZone);

  return {
    year: zoned.getFullYear(),
    month: zoned.getMonth() + 1,
    day: zoned.getDate(),
    weekday: zoned.getDay(),
    hour: zoned.getHours(),
    minute: zoned.getMinutes(),
    second: zoned.getSeconds(),
  };
};

export const getSecondsSinceMidnight = (
  dateParts: Pick<TimeZoneDateParts, 'hour' | 'minute' | 'second'>,
): number =>
  dateParts.hour * SECONDS_PER_HOUR +
  dateParts.minute * SECONDS_PER_MINUTE +
  dateParts.second;

/**
 * Whether `timeZone` is a recognized IANA identifier.
 *
 * We use a fixed format locale (`en-US`) for the probe `Intl.DateTimeFormat`
 * because only the `timeZone` option determines acceptance; locale affects
 * formatted output (as in `formatTimeZoneDisplayName`), not whether the id is
 * valid.
 */
export const isValidTimeZone = (
  timeZone: string | null | undefined,
): timeZone is string => {
  if (!timeZone) {
    return false;
  }
  if (validatedTimeZoneCache.has(timeZone)) {
    return true;
  }
  // Intl.DateTimeFormat throws a RangeError for unrecognized timezone
  // identifiers, so try/catch is the standard way to validate them.
  try {
    // eslint-disable-next-line no-new -- construction-side effect is the validation.
    new Intl.DateTimeFormat('en-US', { timeZone });
    validatedTimeZoneCache.add(timeZone);
    return true;
  } catch {
    return false;
  }
};

export const resolveLocationTimeZone = (
  timeZone: string | null | undefined,
): string =>
  isValidTimeZone(timeZone) ? timeZone : DEFAULT_LOCATION_TIME_ZONE;

/**
 * Human-readable zone label for an IANA id, e.g.
 * `"Pacific Daylight Time (PDT)"` for `America/Los_Angeles` in summer.
 */
export const formatTimeZoneDisplayName = (
  timeZone: string | null | undefined,
  options?: { locale?: string },
): string => {
  const now = new Date();
  const locale = options?.locale ?? 'en-US';
  const resolved = resolveLocationTimeZone(timeZone);

  const timeZoneNamePart = (
    timeZoneName: Intl.DateTimeFormatOptions['timeZoneName'],
  ): string | undefined =>
    new Intl.DateTimeFormat(locale, {
      timeZone: resolved,
      timeZoneName,
    })
      .formatToParts(now)
      .find((part) => part.type === 'timeZoneName')?.value;

  const longName = timeZoneNamePart('long');
  const shortName = timeZoneNamePart('short');

  if (longName && shortName && longName !== shortName) {
    return `${longName} (${shortName})`;
  }

  return longName ?? shortName ?? resolved;
};

/**
 * The environment default IANA timezone (from `resolvedOptions().timeZone`).
 *
 * No app/user locale is passed: locale does not change which zone the
 * runtime reports for the default calendar; it only affects how dates in
 * that zone are labeled when formatting.
 */
export const getUserTimeZone = (): string =>
  resolveLocationTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);

export const getLocalWeekdayAndSecondsInTimeZone = (
  timeZone: string,
  now: Date = new Date(),
): { dayOfWeek: number; secondsSinceMidnight: number } => {
  const parts = getDatePartsInTimeZone(now, timeZone);
  return {
    dayOfWeek: parts.weekday,
    secondsSinceMidnight: getSecondsSinceMidnight(parts),
  };
};

/**
 * Shifts a calendar date (year/month/day) by the given number of days.
 * We operate in UTC throughout so that DST changes in any local timezone
 * don't cause the "add one day" operation to land on the wrong date.
 */
const shiftYmdByDays = (
  dateParts: Pick<TimeZoneDateParts, 'year' | 'month' | 'day'>,
  days: number,
): Pick<TimeZoneDateParts, 'year' | 'month' | 'day'> => {
  const baseDate = new Date(
    Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day),
  );
  const shiftedDate = addDays(baseDate, days);
  return {
    year: shiftedDate.getUTCFullYear(),
    month: shiftedDate.getUTCMonth() + 1,
    day: shiftedDate.getUTCDate(),
  };
};

/**
 * Combines today's calendar date (in `timeZone`), a day offset, and a wall
 * clock time into an absolute UTC Date.
 */
export const buildInstantForZonedDay = (
  todayParts: TimeZoneDateParts,
  daysFromToday: number,
  secondsSinceMidnight: number,
  timeZone: string,
): Date => {
  const targetYmd = shiftYmdByDays(todayParts, daysFromToday);
  const hours = Math.floor(secondsSinceMidnight / SECONDS_PER_HOUR);
  const minutes = Math.floor(
    (secondsSinceMidnight % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE,
  );
  const seconds = secondsSinceMidnight % SECONDS_PER_MINUTE;

  const zoned = new TZDateMini(
    targetYmd.year,
    targetYmd.month - 1,
    targetYmd.day,
    hours,
    minutes,
    seconds,
    timeZone,
  );
  return new Date(zoned.getTime());
};
